"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle2, CircleAlert, PenLine, Wallet as WalletIcon } from "lucide-react";
import { signatureKindOf, type SignRequiredStep } from "@/features/finance/external-signing";
import { describeFinanceSigningStatus } from "@/features/finance/hooks/useExternalFinanceSigning";
import { Spinner } from "@/features/home-v2/ui";
import { Note } from "@/features/wallet/v2/dialogs/kit";
import type { Handoff } from "../../flows/shared";

/** What the wallet is about to be asked for, in the holder's words — each is its own popup. */
function describeStep(step: SignRequiredStep, isFa: boolean): string {
  switch (step) {
    case "OFFER_WRAP_REQUIRED":
      return isFa ? "مرحله‌ی اول: تبدیل POL به WPOL. آفر فقط با WPOL ثبت می‌شود." : "Step one: wrap POL into WPOL. Offers can only be made in WPOL.";
    case "OFFER_APPROVE_REQUIRED":
      return isFa ? "اجازه‌ی برداشت WPOL به مارکت‌پلیس داده می‌شود. مبلغ آفر همین حالا کسر نمی‌شود." : "Allow the marketplace to spend your WPOL. The offer amount is not taken now.";
    case "OFFER_SIGN_REQUIRED":
      return isFa ? "مرحله‌ی آخر: امضای خود آفر. این یک امضاست، نه تراکنش — کارمزدی ندارد." : "Last step: sign the offer itself. This is a signature, not a transaction — it costs nothing.";
    case "OFFER_CANCEL_REQUIRED":
      return isFa ? "لغو آفر روی بلاکچین ثبت می‌شود." : "The cancellation is recorded on-chain.";
    case "OFFER_ACCEPT_APPROVE_REQUIRED":
      return isFa ? "مرحله‌ی اول: اجازه‌ی انتقال این NFT به مارکت‌پلیس داده می‌شود." : "Step one: allow the marketplace to transfer this NFT.";
    case "OFFER_ACCEPT_REQUIRED":
      return isFa ? "مرحله‌ی آخر: تسویه‌ی نهایی. مالکیت با همین تراکنش منتقل می‌شود." : "Last step: the settlement. Ownership transfers with this transaction.";
    case "LISTING_APPROVE_REQUIRED":
      return isFa ? "مرحله‌ی اول: اجازه‌ی فروش این NFT به مارکت‌پلیس داده می‌شود." : "Step one: allow the marketplace to sell this NFT.";
    case "LISTING_SIGN_REQUIRED":
      return isFa ? "مرحله‌ی آخر: امضای سفارش فروش. این یک امضاست، نه تراکنش — کارمزدی ندارد." : "Last step: sign the sale order. This is a signature, not a transaction — it costs nothing.";
    case "LISTING_CANCEL_REQUIRED":
      return isFa ? "لغو لیست روی بلاکچین ثبت می‌شود." : "The cancellation is recorded on-chain.";
    case "FUND_DEPLOYER":
      return isFa ? "این یک انتقال ساده برای پرداخت کارمزد ساخت است، نه ساخت کالکشن. کالکشن شما ساخته شده و بعد از تایید این تراکنش منتشر می‌شود." : "This is a plain transfer that pays the creation fee — it does not create the collection. Your collection already exists and is published once this confirms.";
  }
}

/**
 * THE USER'S OWN WALLET, in v2 — the legacy `ExternalWalletSignPanel`'s job
 * in the money dialogs' grammar (connect MetaMask / Trust Wallet, whether the
 * connected account is the expected one, the network it must be on, what is
 * happening now, the error, a note that is not a failure, and the hashes
 * already sent — retrying never sends them again). Shown only after the
 * backend asked for a round; the site's own wallet never sees it.
 */
export function SignPanel(t0) {
  const $ = _c(56);
  const {
    handoff,
    networkLabel,
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const pending = handoff.pending;
  if (!pending) {
    return null;
  }
  const s = handoff.signing;
  const connecting = s.status === "connecting";
  let t2;
  if ($[2] !== isFa || $[3] !== s.status) {
    t2 = describeFinanceSigningStatus(s.status, isFa);
    $[2] = isFa;
    $[3] = s.status;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const progress = t2;
  let t3;
  if ($[5] !== pending.step) {
    t3 = signatureKindOf(pending.step);
    $[5] = pending.step;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const free = t3 === "typed_data";
  const from = pending.signPayload.from;
  let t4;
  if ($[7] !== from || $[8] !== s.connectedAddress) {
    t4 = s.connectedAddress && from ? s.connectedAddress.toLowerCase() === from.toLowerCase() : null;
    $[7] = from;
    $[8] = s.connectedAddress;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const matches = t4;
  let t5;
  if ($[10] !== t) {
    t5 = t("\u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0634\u0645\u0627", "Your wallet");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== free) {
    t6 = free ? <PenLine aria-hidden={true} /> : <WalletIcon aria-hidden={true} />;
    $[12] = free;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("\u0627\u06CC\u0646 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u062F\u0631 \u0627\u062E\u062A\u06CC\u0627\u0631 \u062E\u0648\u062F \u0634\u0645\u0627\u0633\u062A\u060C \u067E\u0633 \u0647\u0631 \u0645\u0631\u062D\u0644\u0647 \u0631\u0627 \u0628\u0627\u06CC\u062F \u062F\u0631 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u062E\u0648\u062F\u062A\u0627\u0646 \u062A\u0627\u06CC\u06CC\u062F \u06A9\u0646\u06CC\u062F.", "This is your own wallet, so every step has to be approved in it.");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isFa || $[17] !== pending.step) {
    t8 = describeStep(pending.step, isFa);
    $[16] = isFa;
    $[17] = pending.step;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <span>{t7} {t8}</span>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t6 || $[23] !== t9) {
    t10 = <p className="nd-sign-lead">{t6}{t9}</p>;
    $[22] = t6;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== connecting || $[26] !== from || $[27] !== matches || $[28] !== s || $[29] !== t) {
    t11 = !s.connectedAddress ? <div className="md-field"><span className="md-label">{t("\u06A9\u06CC\u0641 \u067E\u0648\u0644 \u062E\u0648\u062F \u0631\u0627 \u0645\u062A\u0635\u0644 \u06A9\u0646\u06CC\u062F", "Connect your wallet")}</span><div className="md-wallets">{(["metamask", "trustwallet"] as const).map(w => <button key={w} type="button" className="md-wallet v2-focus" onClick={() => void s.connect(w)} disabled={connecting} aria-busy={connecting && s.activeWallet === w || undefined}>{connecting && s.activeWallet === w ? <Spinner /> : <WalletIcon aria-hidden={true} />}<b>{w === "metamask" ? "MetaMask" : "Trust Wallet"}</b></button>)}</div></div> : <div className="md-connected" data-match={matches === true ? "yes" : matches === false ? "no" : undefined}><span className="md-label">{t("\u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0645\u062A\u0635\u0644", "Connected wallet")}</span><code dir="ltr">{s.connectedAddress}</code>{matches === false ? <p><CircleAlert aria-hidden={true} />{t(`این آدرس با آدرس مورد انتظار یکسان نیست: ${from}. در کیف پول خود به این حساب تغییر دهید، سپس دوباره تلاش کنید.`, `This does not match the expected address: ${from}. Switch to that account in your wallet, then try again.`)}</p> : matches === true ? <p><CheckCircle2 aria-hidden={true} />{t("\u0627\u06CC\u0646 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0628\u0627 \u0622\u062F\u0631\u0633 \u0645\u0648\u0631\u062F \u0627\u0646\u062A\u0638\u0627\u0631 \u06CC\u06A9\u0633\u0627\u0646 \u0627\u0633\u062A.", "This wallet matches the expected address.")}</p> : null}</div>;
    $[25] = connecting;
    $[26] = from;
    $[27] = matches;
    $[28] = s;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t) {
    t12 = t("\u0634\u0628\u06A9\u0647\u200C\u06CC \u0645\u0648\u0631\u062F \u0646\u06CC\u0627\u0632: ", "Required network: ");
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== networkLabel) {
    t13 = <b>{networkLabel}</b>;
    $[33] = networkLabel;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <p className="md-after">{t12}{t13}</p>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== progress) {
    t15 = progress ? <p className="md-status"><Spinner />{progress}</p> : null;
    $[38] = progress;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== s.error) {
    t16 = s.error ? <Note tone="alert">{s.error}</Note> : null;
    $[40] = s.error;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== handoff.indexingNotice) {
    t17 = handoff.indexingNotice ? <Note>{handoff.indexingNotice}</Note> : null;
    $[42] = handoff.indexingNotice;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== s.trxHashes || $[45] !== t) {
    t18 = s.trxHashes.length > 0 ? <div className="md-connected"><span className="md-label">{t("\u062A\u0631\u0627\u06A9\u0646\u0634\u200C\u0647\u0627\u06CC \u0627\u0631\u0633\u0627\u0644\u200C\u0634\u062F\u0647", "Transactions sent")}</span>{s.trxHashes.map(_temp)}<p>{t("\u0627\u06CC\u0646 \u062A\u0631\u0627\u06A9\u0646\u0634\u200C\u0647\u0627 \u0627\u0631\u0633\u0627\u0644 \u0634\u062F\u0647\u200C\u0627\u0646\u062F\u061B \u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647 \u0622\u0646\u200C\u0647\u0627 \u0631\u0627 \u0627\u0632 \u0646\u0648 \u0646\u0645\u06CC\u200C\u0641\u0631\u0633\u062A\u062F.", "These are already sent \u2014 retrying does not send them again.")}</p></div> : null;
    $[44] = s.trxHashes;
    $[45] = t;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t14 || $[50] !== t15 || $[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t5) {
    t19 = <section className="nd-sign" aria-label={t5}>{t10}{t11}{t14}{t15}{t16}{t17}{t18}</section>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t5;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  return t19;
}
function _temp(hash) {
  return <code key={hash} dir="ltr">{hash}</code>;
}